/* SWBFacetsRender: generic facet renderer. Full architecture doc in
   MATERIALS-PAGE/README.md + build/facets-integration.md (kept out of the section
   to stay under Shopify's 256 KB template limit). */

(function (window, document) {
  'use strict';

  if (window.SWBFacetsRender) { return; }

  /* -------------------------------------------------------------------------------------------------------------------
   * Constants read off the theme. Source line for each is in research/facet-markup-contract.md.
   * -----------------------------------------------------------------------------------------------------------------*/

  var PRICE_MIN = 'filter.v.price.gte';
  var PRICE_MAX = 'filter.v.price.lte';

  /* Params we own but never render as their own group. Price is a group, but a two-param one. */
  var PRICE_PARAMS = [PRICE_MIN, PRICE_MAX];

  /* Utility classes option-value.liquid L181 puts next to the colour slug. Everything else on that label is the slug. */
  var SWATCH_NOISE = {
    'color-swatch': 1, 'color-swatch--rectangle': 1, 'color-swatch--sm': 1, 'ring-inset': 1,
    'rounded-full': 1, 'block-swatch': 1, 'sr-only': 1, 'is-disabled': 1
  };

  /* Whether a value's count may be PRINTED. Availability is a different question and is never gated by
     this: a zero still disables a value, because comparing to zero survives a cap that bites in the
     thousands. Set false by a caller whose source does not count truthfully - see setShowCounts. */
  var SHOW_COUNTS = true;

  /* Server-computed availability per facet value: { param: { value: count } }. Filled by
     the section from the mb-facets templates before each render; empty until then, which is
     why every read is guarded rather than assumed. */
  var COUNTS = Object.create(null);

  /* Values above this collapse behind a numeric "+N" toggle, colour included -
     kept, on reflection, because 12 covers most shoppers' pick at a glance and
     the toggle itself now reads as a clear button rather than bare text. */
  var MAX_VISIBLE = 12;
  function limitFor(group) { return MAX_VISIBLE; }

  /* Module-level, deliberately NOT per-instance: both survive re-injection, which is the whole point.
   *   FILLS   colour slug -> { bg: cssColor, img: url }   merged from every response, never cleared
   *   TEXT    param + \0 + value -> label text            so a chip keeps its name after a fetch drops the value */
  var FILLS = {};
  var TEXT = {};

  /* -------------------------------------------------------------------------------------------------------------------
   * Small helpers
   * -----------------------------------------------------------------------------------------------------------------*/

  function all(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) { node.className = cls; }
    if (text != null) { node.textContent = text; }
    return node;
  }

  /** replaceChildren is Safari 14+. The section already relies on it, but fall back anyway. */
  function setKids(host, nodes) {
    if (typeof host.replaceChildren === 'function') { host.replaceChildren.apply(host, nodes); return; }
    while (host.firstChild) { host.removeChild(host.firstChild); }
    var frag = document.createDocumentFragment();
    nodes.forEach(function (n) { frag.appendChild(n); });
    host.appendChild(frag);
  }

  function clean(text) { return String(text == null ? '' : text).replace(/\s+/g, ' ').trim(); }

  function slug(value) {
    return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /** CSS.escape is not universal and these ids legitimately contain dots. Escape the two chars that matter. */
  function attrValue(value) { return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"'); }

  /**
   * The <label> that belongs to `input`, tried cheapest first.
   * checkbox.liquid puts the label AFTER the input inside .checkbox-container; option-value.liquid puts it after the
   * sr-only input; facets-vertical.liquid's boolean puts it BEFORE. All four shapes are covered.
   */
  function labelFor(input) {
    var wrapping = input.closest ? input.closest('label') : null;
    if (wrapping) { return wrapping; }

    var id = input.getAttribute('id');
    if (id) {
      var scope = input.parentNode || input.ownerDocument;
      var near = scope.querySelector ? scope.querySelector('label[for="' + attrValue(id) + '"]') : null;
      if (near) { return near; }
      var far = input.ownerDocument.querySelector('label[for="' + attrValue(id) + '"]');
      if (far) { return far; }
    }

    var next = input.nextElementSibling;
    if (next && next.tagName === 'LABEL') { return next; }
    var prev = input.previousElementSibling;
    if (prev && prev.tagName === 'LABEL') { return prev; }
    return null;
  }

  /* `Leer (9462)`, `5,5 - 8,5 cm (12)`. Trailing parenthesised run of digits/separators only. */
  var COUNT_RE = /\((\d[\d.,  \s]*)\)\s*$/;

  function splitCount(text) {
    var raw = clean(text);
    var match = raw.match(COUNT_RE);
    if (!match) { return { label: raw, count: null, zero: false }; }
    var digits = match[1].replace(/[^\d]/g, '');
    return {
      label: clean(raw.slice(0, match.index)),
      count: clean(match[1]),
      zero: digits === '' || parseInt(digits, 10) === 0
    };
  }

  /**
   * Scrape `.color-swatch.<slug> { background-color: X }` / `background-image: url(Y)` out of every <style> in the
   * fetched document and merge it into FILLS. Both source sections emit that loop from the product_variant_colors
   * metaobject (main-collection.liquid L641-651, main-search.liquid L241-254), so this is market-correct by
   * construction and needs no Liquid on our side.
   */
  var RULE_RE = /\.color-swatch\.([^\s{,.:;>+~()[\]]+)\s*\{([^}]*)\}/gi;

  function parseFills(doc) {
    all(doc, 'style').forEach(function (style) {
      var css = style.textContent || '';
      if (css.indexOf('.color-swatch.') === -1) { return; }
      RULE_RE.lastIndex = 0;
      var hit;
      while ((hit = RULE_RE.exec(css)) !== null) {
        var key = hit[1].toLowerCase();
        var body = hit[2];
        var entry = FILLS[key] || (FILLS[key] = {});
        var bg = body.match(/background(?:-color)?\s*:\s*([^;!}]+)/i);
        if (bg) { entry.bg = clean(bg[1]); }
        var img = body.match(/background-image\s*:\s*url\(\s*['"]?([^'")]+)/i);
        if (img) { entry.img = clean(img[1]); }
      }
    });
  }

  function fillFor(swatchSlug) {
    if (!swatchSlug) { return null; }
    var entry = FILLS[swatchSlug.toLowerCase()];
    if (!entry) { return null; }
    if (!entry.bg && !entry.img) { return null; }
    return entry;
  }

  function fillsKnown() {
    for (var key in FILLS) { if (Object.prototype.hasOwnProperty.call(FILLS, key)) { return true; } }
    return false;
  }

  /** #fff / #ffffff / rgb() -> is it light enough to need a visible border? Best effort, hex only. */
  function isLight(color) {
    if (!color) { return false; }
    var hex = color.replace(/^#/, '');
    if (hex.length === 3) { hex = hex.charAt(0) + hex.charAt(0) + hex.charAt(1) + hex.charAt(1) + hex.charAt(2) + hex.charAt(2); }
    if (!/^[0-9a-f]{6}$/i.test(hex)) { return false; }
    var r = parseInt(hex.slice(0, 2), 16), g = parseInt(hex.slice(2, 4), 16), b = parseInt(hex.slice(4, 6), 16);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 218;
  }

  /* -------------------------------------------------------------------------------------------------------------------
   * READING THE FETCHED DOCUMENT
   * -----------------------------------------------------------------------------------------------------------------*/

  /* The fetched response carries the facets snippet twice (drawer + inline); take one. */
  function pickForm(doc) {
    return doc.querySelector('form.facets-vertical.filter-form') ||
           doc.querySelector('form.facets-vertical') ||
           doc.querySelector('[id^="shopify-section-"]') ||
           doc.body ||
           doc;
  }

  /**
   * Every facet group in document order, which is the merchandising order set in Shopify admin.
   *   details.accordion   list / swatch / price / wristsize   (via snippets/accordion.liquid or facets-wristsize)
   *   div.accordion       boolean                             (facets-vertical.liquid L82, NO <details>)
   * Nested nodes are skipped so a group is never counted twice.
   */
  function groupNodes(form) {
    var nodes = all(form, 'details.accordion, details, div.accordion');
    var out = [];
    nodes.forEach(function (node) {
      for (var i = 0; i < out.length; i++) { if (out[i].contains(node)) { return; } }
      if (!node.querySelector('input[name]')) { return; }
      out.push(node);
    });
    return out;
  }

  /* The theme's own accordion header embeds a <filter-information> info-icon next to
     several labels (Polsmaat, Sluiting confirmed live) - a tooltip trigger carrying its
     OWN duplicate label, its own help copy, and a same-instance <style> tag. textContent
     never excludes <style>/<script> descendants, so reading a title node's textContent
     directly pulled in the label twice, the tooltip's help text, and the raw CSS source
     as visible page text. Strip that subtree before reading the label. */
  function labelText(node) {
    if (!node) { return ''; }
    var clone = node.cloneNode(true);
    var junk = clone.querySelectorAll('filter-information, style, script');
    for (var i = 0; i < junk.length; i++) { junk[i].remove(); }
    return clean(clone.textContent);
  }

  function groupTitle(node, type) {
    if (type === 'boolean') {
      var boolLabel = node.querySelector('.accordion__toggle label, label.bold, label');
      if (boolLabel) { return labelText(boolLabel); }
    }
    var titled = node.querySelector('.accordion__toggle-title');
    if (titled) { return labelText(titled); }
    var summary = node.querySelector('summary');
    if (summary) { return labelText(summary); }
    return '';
  }

  function detectType(node, inputs) {
    if (node.querySelector('price-range, .price-range') || node.querySelector('[name="' + PRICE_MIN + '"]')) {
      return 'price';
    }
    if (node.querySelector('.wristsize-hidden-checkboxes, [data-wristsize-field]')) { return 'wristsize'; }
    if (node.querySelector('label.color-swatch')) { return 'swatch'; }
    if (inputs.length === 1 && (inputs[0].classList.contains('switch') || inputs[0].value === '1')) { return 'boolean'; }
    return 'list';
  }

  /** One value of a list / swatch / wristsize group. */
  function readValue(input) {
    var label = labelFor(input);
    var parsed = splitCount(label ? label.textContent : input.value);
    var swatchLabel = label && label.classList.contains('color-swatch') ? label : null;
    var swatchSlug = '';

    if (swatchLabel) {
      /* option-value.liquid L181: class="color-swatch { { label | downcase | replace: ' ', '-' }} ...". The colour slug
         is whatever is left once the known utility classes are removed. */
      Array.prototype.forEach.call(swatchLabel.classList, function (cls) {
        if (!SWATCH_NOISE[cls] && !swatchSlug) { swatchSlug = cls; }
      });
      if (!parsed.label) {
        var sr = swatchLabel.querySelector('.sr-only');
        parsed.label = clean(sr ? sr.textContent : swatchLabel.getAttribute('data-tooltip'));
      }
    }

    return {
      value: input.getAttribute('value') || '',
      label: parsed.label || clean(input.getAttribute('value')),
      count: parsed.count,
      /* Disabled is the SERVER's call (count 0 and not active). parsed.zero is only a fallback for a theme that ever
         stops emitting the attribute. */
      disabled: input.disabled || input.hasAttribute('disabled') || (parsed.zero && !input.checked),
      swatch: swatchSlug
    };
  }

  /** Everything the renderer needs about one group, with no reference to the fetched DOM left over. */
  function describe(node) {
    var inputs = all(node, 'input[name^="filter."]').filter(function (input) {
      return input.type !== 'hidden';
    });
    if (!inputs.length) { return null; }

    var type = detectType(node, inputs);
    var param = type === 'price' ? 'filter.v.price' : inputs[0].getAttribute('name');
    var group = { param: param, type: type, title: groupTitle(node, type), values: [], meta: {} };

    if (type === 'price') {
      var lo = node.querySelector('[name="' + PRICE_MIN + '"]');
      var hi = node.querySelector('[name="' + PRICE_MAX + '"]');
      var ranges = all(node, 'input[type="range"]');
      /* price-range.liquid L16-22 derives range_max from the CURRENT result set, so it changes on every render.
         Never cache it - read it fresh, here, every time. */
      var ceiling = hi && hi.getAttribute('max') ? hi.getAttribute('max')
                  : (ranges.length ? ranges[ranges.length - 1].getAttribute('max') : '');
      group.meta.ceiling = clean(ceiling);
      group.meta.currency = clean((node.querySelector('.prefix') || {}).textContent);
      group.meta.serverMin = lo ? clean(lo.getAttribute('value')) : '';
      group.meta.serverMax = hi ? clean(hi.getAttribute('value')) : '';
      return group;
    }

    if (type === 'wristsize') {
      var field = node.querySelector('[data-wristsize-field]');
      group.meta.unit = clean(field ? field.getAttribute('data-unit') : '');
      group.meta.fieldLabel = clean((node.querySelector('.wristsize-field__label') || {}).textContent);
      group.meta.placeholder = clean((node.querySelector('.wristsize-field__input') || {}).placeholder);
      /* facets-wristsize.liquid L76 renders the value labels WITHOUT a count capture, so these are bare band labels
         like `5.5 - 8.5 inch`. bandOf() parses the two numbers back out of them. */
    }

    if (type === 'boolean') {
      group.values.push({ value: inputs[0].getAttribute('value') || '1', label: group.title, count: null, disabled: false, swatch: '' });
      return group;
    }

    var seen = {};
    inputs.forEach(function (input) {
      var value = input.getAttribute('value') || '';
      if (!value || seen[value]) { return; }
      seen[value] = 1;
      group.values.push(readValue(input));
    });
    return group;
  }

  /** `5.5 - 8.5 inch` / `16,5 - 19 cm` -> { min: 5.5, max: 8.5 }. */
  function bandOf(text) {
    var numbers = clean(text).match(/\d+(?:[.,]\d+)?/g);
    if (!numbers || numbers.length < 2) { return null; }
    return { min: parseFloat(numbers[0].replace(',', '.')), max: parseFloat(numbers[1].replace(',', '.')) };
  }

  /* -------------------------------------------------------------------------------------------------------------------
   * FACTORY
   * -----------------------------------------------------------------------------------------------------------------*/

  function create(cfg) {
    cfg = cfg || {};
    if (!cfg.host) { throw new Error('SWBFacetsRender: cfg.host is required.'); }
    if (typeof cfg.onChange !== 'function') { throw new Error('SWBFacetsRender: cfg.onChange is required.'); }

    var host   = cfg.host;
    var sid    = cfg.sid || 'x';
    var store  = cfg.store || {};              /* MUTATED IN PLACE. Same object identity for the section's life. */
    var labels = cfg.labels || {};
    var onChange = cfg.onChange;

    var EXCLUDE = {};
    (cfg.exclude || []).forEach(function (param) { EXCLUDE[param] = 1; });

    var OPEN = {};        /* param -> bool. Keyed by PARAM, never by id, never by section id. */
    var MORE = {};        /* param -> bool. "+N" expanded state, same keying. */
    var WRIST = {};       /* param -> the raw number the visitor typed. Display only; not in the URL. */
    var LAST = {};        /* param -> group descriptor from the last render, so chips() can name a vanished value. */
    var MINTED = {};      /* id stem -> final id. See uid(). */
    var bound = false;

    /* ---------------------------------------------------------------------------------------------------------------
     * STORE
     * -------------------------------------------------------------------------------------------------------------*/

    function get(param) { return store[param] || []; }

    function isOn(param, value) { return get(param).indexOf(value) !== -1; }

    function setValues(param, values) {
      if (!values || !values.length) { delete store[param]; return; }
      store[param] = values.slice();
    }

    function toggle(param, value) {
      var values = get(param).slice();
      var at = values.indexOf(value);
      if (at === -1) { values.push(value); } else { values.splice(at, 1); }
      setValues(param, values);
    }

    function activeCount(param) { return get(param).length; }

    function groupType(param) { return LAST[param] ? LAST[param].type : ''; }

    /**
     * Counted the way a visitor counts, not the way the query string does:
     *   the two price params are ONE filter;
     *   wrist size is ONE filter even though one typed number resolves to several band values.
     * Before the first render `LAST` is empty, so a wrist-size value restored from the URL counts as its band count
     * until the first response lands. Harmless - the badge corrects itself on the same load.
     */
    function count() {
      var total = 0, priced = false;
      Object.keys(store).forEach(function (param) {
        var values = store[param];
        if (!values || !values.length) { return; }
        if (param === PRICE_MIN || param === PRICE_MAX) { priced = true; return; }
        total += groupType(param) === 'wristsize' ? 1 : values.length;
      });
      return total + (priced ? 1 : 0);
    }

    function remember(param, value, text) {
      if (text) { TEXT[param + '' + value] = text; }
    }

    function nameOf(param, value) {
      return TEXT[param + '' + value] || value;
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * PUBLIC STATE API - what the section uses in buildUrl / readUrl / renderChips
     * -------------------------------------------------------------------------------------------------------------*/

    function allValues() {
      var out = {};
      Object.keys(store).forEach(function (param) {
        if (store[param] && store[param].length) { out[param] = store[param].slice(); }
      });
      return out;
    }

    /** Replace the whole store, keeping the SAME object identity so buildUrl's reference stays valid. */
    function setAll(next) {
      Object.keys(store).forEach(function (param) { delete store[param]; });
      Object.keys(next || {}).forEach(function (param) {
        if (EXCLUDE[param]) { return; }
        var values = next[param];
        if (!values) { return; }
        if (!Array.isArray(values)) { values = [values]; }
        values = values.filter(function (value) { return value !== '' && value != null; });
        if (values.length) { store[param] = values.map(String); }
      });
    }

    function clearAll(silent) {
      Object.keys(store).forEach(function (param) { delete store[param]; });
      Object.keys(WRIST).forEach(function (param) { delete WRIST[param]; });
      if (!silent) { emit('clear-all', null); }
    }

    function clearGroup(param, silent) {
      if (param === 'filter.v.price') {
        delete store[PRICE_MIN];
        delete store[PRICE_MAX];
      } else {
        delete store[param];
        delete WRIST[param];
      }
      if (!silent) { emit('clear-group', param); }
    }

    /**
     * One entry per active value, ready for the section's chip row. `label` survives a fetch in which the value
     * disappeared from the response, because TEXT is module-level and merged.
     */
    function chips() {
      var out = [];
      var min = get(PRICE_MIN)[0] || '';
      var max = get(PRICE_MAX)[0] || '';

      Object.keys(store).forEach(function (param) {
        if (param === PRICE_MIN || param === PRICE_MAX) { return; }
        var group = LAST[param];
        var values = get(param);

        /* One typed wrist size becomes several band values server-side. Showing three chips for one number the
           visitor typed once, each removable on its own, is worse than useless - it is not undoable. One chip. */
        if (group && group.type === 'wristsize') {
          var typed = clean(WRIST[param] || '');
          var unit = clean(group.meta.unit || '');
          var bands = values.map(function (value) { return nameOf(param, value); });
          var shown = typed ? typed + (unit ? ' ' + unit : '')
                            : bands[0] + (bands.length > 1 ? ' +' + (bands.length - 1) : '');
          out.push({
            param: param,
            value: values.join('|'),
            label: clean((group.title || param) + ' ' + shown),
            remove: function () { clearGroup(param, true); emit('remove', param); }
          });
          return;
        }

        values.forEach(function (value) {
          var text = nameOf(param, value);
          if (group && group.type === 'boolean') { text = group.title || text; }
          out.push({
            param: param,
            value: value,
            label: text,
            remove: function () { toggle(param, value); emit('remove', param); }
          });
        });
      });

      if (min || max) {
        out.push({
          param: 'filter.v.price',
          value: min + '-' + max,
          label: (labels.price || 'Price') + ' ' + (min || '0') + '-' + (max || '∞'),
          remove: function () { clearGroup('filter.v.price', true); emit('remove', 'filter.v.price'); }
        });
      }
      return out;
    }

    function emit(reason, param) {
      onChange({ reason: reason, param: param || null, count: count() });
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * ID MINTING - guard 2 against the accordion-{param} collision
     * -------------------------------------------------------------------------------------------------------------*/

    /**
     * `id` already held by someone who is not us. An element INSIDE our own host is the previous render's node, which
     * is about to be replaced, so it is not a clash - without that carve-out every re-render would mint a new suffix
     * and aria-controls would churn forever.
     */
    function takenElsewhere(id) {
      for (var stem in MINTED) {
        if (Object.prototype.hasOwnProperty.call(MINTED, stem) && MINTED[stem] === id) { return true; }
      }
      var found = document.getElementById(id);
      return !!(found && !host.contains(found));
    }

    function uid(base) {
      var stem = 'mbf-' + sid + '-' + slug(base);
      /* Stable per stem, so the same group keeps the same id across renders. */
      if (MINTED[stem]) { return MINTED[stem]; }
      var candidate = stem;
      var n = 0;
      /* Guard against the theme's own `accordion-{param}` ids (facets-vertical.liquid L152 - no section id in them),
         against another copy of this section, and against apps. */
      while (takenElsewhere(candidate)) {
        n += 1;
        candidate = stem + '-' + n;
      }
      MINTED[stem] = candidate;
      return candidate;
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * CONTROL BUILDERS
     * -------------------------------------------------------------------------------------------------------------*/

    function valueButton(group, item) {
      var node = el('button', 'mb-c mb-fv');
      node.type = 'button';
      node.setAttribute('data-mbf-v', '');
      node.setAttribute('data-param', group.param);
      node.setAttribute('data-value', item.value);
      node.setAttribute('aria-pressed', isOn(group.param, item.value) ? 'true' : 'false');

      node.appendChild(el('span', 'mb-fv__t', item.label));
      if (item.count && SHOW_COUNTS) { node.appendChild(el('span', 'mb-fv__n', item.count)); }

      if (item.disabled && !isOn(group.param, item.value)) {
        node.disabled = true;
        node.setAttribute('aria-disabled', 'true');
      }
      remember(group.param, item.value, item.label);
      return node;
    }

    function swatchButton(group, item) {
      var fill = fillFor(item.swatch);

      /* Fill chain step 2: the metaobject has no colour for this title, and other titles DO have one, so the miss is
         real. A word beats an invisible swatch. */
      if (!fill && fillsKnown()) { return valueButton(group, item); }

      var node = el('button', 'mb-sw color-swatch' + (item.swatch ? ' ' + item.swatch : ''));
      node.type = 'button';
      node.setAttribute('data-mbf-v', '');
      node.setAttribute('data-param', group.param);
      node.setAttribute('data-value', item.value);
      node.setAttribute('aria-pressed', isOn(group.param, item.value) ? 'true' : 'false');
      node.title = item.count && SHOW_COUNTS ? item.label + ' (' + item.count + ')' : item.label;
      node.setAttribute('aria-label', node.title);
      /* Same attribute the theme's own option-value.liquid emits, so theme.css's global
         [data-tooltip] rule gives this swatch the identical tooltip a collection-page
         swatch gets. Plain label, without the count suffix `title` carries. */
      node.setAttribute('data-tooltip', item.label || '');

      if (fill) {
        /* Inline + important, because assets/variant-colors.css L300-305 is `.color-swatch { background:#eee
           !important }` and a plain inline background would LOSE to it. */
        if (fill.bg) {
          node.style.setProperty('background-color', fill.bg, 'important');
          if (isLight(fill.bg)) { node.classList.add('is-light'); }
        }
        if (fill.img) {
          node.style.setProperty('background-image', 'url("' + fill.img + '")', 'important');
          node.style.setProperty('background-size', 'cover', 'important');
        }
      } else {
        /* Fill chain step 3: no map at all. Keep the theme classes and let the transplanted CSS paint it. */
        node.setAttribute('data-swatch-fill', 'css');
      }

      if (item.disabled && !isOn(group.param, item.value)) {
        node.disabled = true;
        node.setAttribute('aria-disabled', 'true');
      }
      node.appendChild(el('span', 'mb-sr', node.title));
      remember(group.param, item.value, item.label);
      return node;
    }

    function booleanToggle(group) {
      var item = group.values[0];
      var on = isOn(group.param, item.value);
      var node = el('button', 'mb-tog');
      node.type = 'button';
      node.setAttribute('role', 'switch');
      node.setAttribute('aria-checked', on ? 'true' : 'false');
      node.setAttribute('data-mbf-v', '');
      node.setAttribute('data-param', group.param);
      node.setAttribute('data-value', item.value);
      node.appendChild(el('span', 'mb-tog__track'));
      node.appendChild(el('span', 'mb-tog__t', group.title || item.label));
      remember(group.param, item.value, group.title || item.label);
      return node;
    }

    function priceControls(group) {
      var row = el('div', 'mb-price');
      var lo = document.createElement('input');
      var hi = document.createElement('input');

      [lo, hi].forEach(function (input) {
        input.type = 'number';
        input.min = '0';
        input.inputMode = 'numeric';
        input.autocomplete = 'off';
        input.setAttribute('data-mbf-price', '');
        input.setAttribute('data-param', group.param);
      });
      lo.setAttribute('data-bound', 'min');
      hi.setAttribute('data-bound', 'max');

      var ceiling = group.meta.ceiling;
      lo.placeholder = '0';
      hi.placeholder = ceiling || '';
      if (ceiling) { lo.max = ceiling; hi.max = ceiling; }
      lo.value = get(PRICE_MIN)[0] || '';
      hi.value = get(PRICE_MAX)[0] || '';
      lo.setAttribute('aria-label', (group.title || labels.price || 'Price') + ' min');
      hi.setAttribute('aria-label', (group.title || labels.price || 'Price') + ' max');

      row.appendChild(lo);
      row.appendChild(el('span', 'mb-price__sep', '-'));
      row.appendChild(hi);
      if (group.meta.currency) { row.appendChild(el('span', 'mb-price__cur', group.meta.currency)); }
      return row;
    }

    /**
     * Wrist size. facets-wristsize.liquid's own behaviour is a plain <script>, which never executes after the response
     * has been parsed with DOMParser, so the control has to be ours. The SUBMITTED value is still the theme's: the
     * band label, e.g. `5.5 - 8.5 inch`, and several bands are active at once - every band that contains the typed
     * number. That is why bands come from the response and are never computed.
     */
    function wristControls(group) {
      var wrap = el('div', 'mb-wrist');
      wrap.setAttribute('data-mbf-wrist', '');
      wrap.setAttribute('data-param', group.param);

      var rowNode = el('div', 'mb-wrist__row');
      var input = document.createElement('input');
      input.type = 'number';
      input.className = 'mb-wrist__in';
      input.min = '1';
      input.max = '99';
      input.step = '0.5';
      input.autocomplete = 'off';
      input.inputMode = 'decimal';
      input.placeholder = group.meta.placeholder || '';
      input.value = WRIST[group.param] || '';
      input.setAttribute('data-mbf-wrist-input', '');
      input.setAttribute('aria-label', group.meta.fieldLabel || group.title || group.param);

      var apply = el('button', 'mb-wrist__go', '→');
      apply.type = 'button';
      apply.setAttribute('data-mbf-wrist-apply', '');
      apply.setAttribute('aria-label', group.meta.fieldLabel || group.title || group.param);

      rowNode.appendChild(input);
      if (group.meta.unit) { rowNode.appendChild(el('span', 'mb-wrist__u', group.meta.unit)); }
      rowNode.appendChild(apply);
      wrap.appendChild(rowNode);

      /* Honest feedback: after a reload the typed number is gone (it is not in the URL - the resolved bands are), so
         show which bands are actually active instead of an empty field with a live filter. */
      var active = get(group.param);
      if (active.length) {
        var names = active.map(function (value) { return nameOf(group.param, value); });
        wrap.appendChild(el('p', 'mb-wrist__fb', names.join(', ')));
      }
      return wrap;
    }

    function applyWrist(wrap) {
      var param = wrap.getAttribute('data-param');
      var input = wrap.querySelector('[data-mbf-wrist-input]');
      var group = LAST[param];
      var raw = input ? clean(input.value) : '';
      var value = parseFloat(raw.replace(',', '.'));

      WRIST[param] = raw;

      if (!raw || isNaN(value)) { clearGroup(param, true); WRIST[param] = ''; emit('wristsize', param); return; }

      var matched = [];
      ((group && group.values) || []).forEach(function (item) {
        if (item.disabled) { return; }                 /* count 0 - the theme's own inline script forgets this */
        var band = bandOf(item.label);
        if (band && value >= band.min && value <= band.max) {
          matched.push(item.value);
          remember(param, item.value, item.label);
        }
      });

      setValues(param, matched);
      emit('wristsize', param);
    }

    function commitPrice(scope) {
      var lo = scope.querySelector('[data-mbf-price][data-bound="min"]');
      var hi = scope.querySelector('[data-mbf-price][data-bound="max"]');
      var min = lo ? clean(lo.value) : '';
      var max = hi ? clean(hi.value) : '';

      /* Swap rather than reject: someone typing 50 then 10 means 10-50, and an empty grid would just look broken. */
      if (min !== '' && max !== '' && parseFloat(min) > parseFloat(max)) {
        var swap = min; min = max; max = swap;
        if (lo) { lo.value = min; }
        if (hi) { hi.value = max; }
      }

      /* theme.js L3396-3402 deletes these params when empty and so must we - Shopify misbehaves on `gte=`. */
      setValues(PRICE_MIN, min === '' ? [] : [min]);
      setValues(PRICE_MAX, max === '' ? [] : [max]);
      emit('price', 'filter.v.price');
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * GROUP SHELL - button + panel, no <details>
     * -------------------------------------------------------------------------------------------------------------*/

    function isOpen(group) {
      if (Object.prototype.hasOwnProperty.call(OPEN, group.param)) { return OPEN[group.param]; }
      /* First sight. Mirror the theme, which hard-codes the colour group open (accordion.liquid L90-92) and leaves the
         rest closed, and additionally open anything the visitor already has a value in. */
      if (group.type === 'swatch') { return true; }
      if (group.param === 'filter.v.price') { return activeCount(PRICE_MIN) + activeCount(PRICE_MAX) > 0; }
      return activeCount(group.param) > 0;
    }

    function buildGroup(group) {
      var open = isOpen(group);
      var panelId = uid(group.param);
      var wrap = el('div', 'mb-fgroup' + (open ? ' is-open' : ''));
      wrap.setAttribute('data-param', group.param);
      wrap.setAttribute('data-type', group.type);

      var active = group.param === 'filter.v.price'
        ? (activeCount(PRICE_MIN) + activeCount(PRICE_MAX) > 0 ? 1 : 0)
        : activeCount(group.param);

      var head = el('div', 'mb-fgroup__hd');
      var trigger = el('button', 'mb-fgroup__t');
      trigger.type = 'button';
      trigger.setAttribute('data-mbf-toggle', '');
      trigger.setAttribute('data-param', group.param);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      trigger.setAttribute('aria-controls', panelId);
      trigger.appendChild(el('span', 'mb-fgroup__n', group.title || group.param));
      if (active) { trigger.appendChild(el('span', 'mb-badge', String(active))); }
      trigger.appendChild(el('span', 'mb-fgroup__ch'));
      head.appendChild(trigger);

      if (active) {
        var clearBtn = el('button', 'mb-fgroup__x', '✕');
        clearBtn.type = 'button';
        clearBtn.setAttribute('data-mbf-clear', '');
        clearBtn.setAttribute('data-param', group.param);
        clearBtn.setAttribute('aria-label', (labels.clear || 'Clear') + ' - ' + (group.title || group.param));
        head.appendChild(clearBtn);
      }
      wrap.appendChild(head);

      var panel = el('div', 'mb-fgroup__p');
      panel.id = panelId;
      if (!open) { panel.hidden = true; }

      if (group.type === 'price') {
        panel.appendChild(priceControls(group));
      } else if (group.type === 'wristsize') {
        panel.appendChild(wristControls(group));
      } else if (group.type === 'boolean') {
        panel.appendChild(booleanToggle(group));
      } else {
        var row = el('div', group.type === 'swatch' ? 'mb-row mb-row--sw' : 'mb-row');
        var expanded = MORE[group.param] === true;
        var limit = limitFor(group);
        var shown = 0;

        /* Colour swatches with zero matches are DROPPED, not greyed. A struck-through word
           ("Rosé Goud") still reads as information - the value exists, just not here - but a
           greyed circle in a swatch grid reads as a colour you can pick, and picking it landed
           the visitor on "Geen producten met deze combinatie". Text-based facets keep the
           disabled-and-visible treatment, where the strikethrough does communicate something.
           An ACTIVE value is always kept: it must stay clickable to be turned off, even in a
           response where its own count came back 0.

           Availability comes from COUNTS (the mb-facets JSON) when the section supplied it.
           It cannot be read from the fetched HTML: the theme's shared facet snippet computes
           `disabled` for swatches and then does not pass it to the renderer, so every colour
           arrives looking available. item.disabled stays as the fallback for any group the
           JSON did not cover. */
        var visible = group.values;
        if (group.type === 'swatch') {
          var counts = COUNTS[group.param];
          visible = group.values.filter(function (item) {
            if (isOn(group.param, item.value)) { return true; }
            if (counts && typeof counts[item.value] === 'number') { return counts[item.value] > 0; }
            return !item.disabled;
          });
        }

        visible.forEach(function (item) {
          var on = isOn(group.param, item.value);
          var over = !expanded && shown >= limit && !on;
          if (over) { return; }
          shown += 1;
          row.appendChild(group.type === 'swatch' ? swatchButton(group, item) : valueButton(group, item));
        });
        panel.appendChild(row);

        /* Counts the values actually OFFERED, so the "+N" toggle never promises colours that
           were filtered out as unavailable. */
        var hiddenCount = visible.length - shown;
        if (hiddenCount > 0 || (expanded && visible.length > limit)) {
          var moreBtn = el('button', 'mb-fgroup__more', expanded ? (labels.showLess || 'Show less') : ('+ ' + hiddenCount + ' ' + (labels.more || 'more')));
          moreBtn.type = 'button';
          moreBtn.setAttribute('data-mbf-more', '');
          moreBtn.setAttribute('data-param', group.param);
          moreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          moreBtn.setAttribute('aria-label', (group.title || group.param) + ' ' + (expanded ? (labels.showLess || 'Show less') : ('+ ' + hiddenCount + ' ' + (labels.more || 'more'))));
          panel.appendChild(moreBtn);
        }
      }

      wrap.appendChild(panel);
      return wrap;
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * RENDER - the drop-in
     * -------------------------------------------------------------------------------------------------------------*/

    function render(doc) {
      if (!doc) { return; }
      bind();
      parseFills(doc);

      var form = pickForm(doc);
      var groups = [];
      LAST = {};

      groupNodes(form).forEach(function (node) {
        var group = describe(node);
        if (!group) { return; }
        if (EXCLUDE[group.param]) { return; }
        if (group.param === 'filter.v.price') {
          /* the two price params are one group; nothing else to skip */
        } else if (PRICE_PARAMS.indexOf(group.param) !== -1) {
          return;
        }
        if (!group.values.length && group.type !== 'price') { return; }
        LAST[group.param] = group;
        groups.push(group);
      });

      var nodes = [];
      groups.forEach(function (group) { nodes.push(buildGroup(group)); });

      if (count() > 0) {
        var foot = el('div', 'mb-fgroup__foot');
        var clearAllBtn = el('button', 'mb-clear-all', labels.clear || 'Clear all');
        clearAllBtn.type = 'button';
        clearAllBtn.setAttribute('data-mbf-clear-all', '');
        foot.appendChild(clearAllBtn);
        nodes.push(foot);
      }

      /* Idempotent: children are REPLACED, so a second render() with the same doc yields the same DOM, and every
         listener lives on `host` (bound once, see bind()), so nothing can double-fire. */
      setKids(host, nodes);
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * DELEGATED EVENTS - bound exactly once per host
     * -------------------------------------------------------------------------------------------------------------*/

    function onClick(event) {
      var target = event.target;
      if (!target || !target.closest) { return; }

      var toggleBtn = target.closest('[data-mbf-toggle]');
      if (toggleBtn && host.contains(toggleBtn)) {
        var param = toggleBtn.getAttribute('data-param');
        var next = toggleBtn.getAttribute('aria-expanded') !== 'true';
        OPEN[param] = next;
        toggleBtn.setAttribute('aria-expanded', next ? 'true' : 'false');
        var panel = document.getElementById(toggleBtn.getAttribute('aria-controls'));
        if (panel) { panel.hidden = !next; }
        var shell = toggleBtn.closest('.mb-fgroup');
        if (shell) { shell.classList[next ? 'add' : 'remove']('is-open'); }
        return;                                     /* pure UI - no fetch */
      }

      var moreBtn = target.closest('[data-mbf-more]');
      if (moreBtn && host.contains(moreBtn)) {
        var moreParam = moreBtn.getAttribute('data-param');
        MORE[moreParam] = !(MORE[moreParam] === true);
        rerenderGroup(moreParam);
        return;                                     /* pure UI - no fetch */
      }

      var valueBtn = target.closest('[data-mbf-v]');
      if (valueBtn && host.contains(valueBtn)) {
        if (valueBtn.disabled) { return; }
        var vParam = valueBtn.getAttribute('data-param');
        var vValue = valueBtn.getAttribute('data-value');
        toggle(vParam, vValue);
        var on = isOn(vParam, vValue);
        if (valueBtn.getAttribute('role') === 'switch') { valueBtn.setAttribute('aria-checked', on ? 'true' : 'false'); }
        else { valueBtn.setAttribute('aria-pressed', on ? 'true' : 'false'); }
        emit('value', vParam);
        return;
      }

      var clearBtn = target.closest('[data-mbf-clear]');
      if (clearBtn && host.contains(clearBtn)) {
        clearGroup(clearBtn.getAttribute('data-param'));
        return;
      }

      if (target.closest('[data-mbf-clear-all]') && host.contains(target.closest('[data-mbf-clear-all]'))) {
        clearAll();
        return;
      }

      var wristBtn = target.closest('[data-mbf-wrist-apply]');
      if (wristBtn && host.contains(wristBtn)) {
        var wrap = wristBtn.closest('[data-mbf-wrist]');
        if (wrap) { applyWrist(wrap); }
      }
    }

    function onChangeEvent(event) {
      var target = event.target;
      if (!target || !target.closest) { return; }
      if (target.hasAttribute && target.hasAttribute('data-mbf-price')) {
        commitPrice(target.closest('.mb-price') || host);
      }
    }

    function onKeydown(event) {
      if (event.key !== 'Enter') { return; }
      var target = event.target;
      if (!target || !target.closest) { return; }

      if (target.hasAttribute && target.hasAttribute('data-mbf-price')) {
        event.preventDefault();
        commitPrice(target.closest('.mb-price') || host);
        return;
      }
      if (target.hasAttribute && target.hasAttribute('data-mbf-wrist-input')) {
        event.preventDefault();
        var wrap = target.closest('[data-mbf-wrist]');
        if (wrap) { applyWrist(wrap); }
      }
    }

    /**
     * A single group re-render, used by the "+N" toggle so opening a long list does not rebuild the panel and lose
     * the caret out of a half-typed price field.
     */
    function rerenderGroup(param) {
      var group = LAST[param];
      if (!group) { return; }
      var current = host.querySelector('.mb-fgroup[data-param="' + attrValue(param) + '"]');
      if (!current) { return; }
      var fresh = buildGroup(group);
      current.parentNode.replaceChild(fresh, current);
    }

    function bind() {
      if (bound || host.__mbfBound) { return; }
      host.addEventListener('click', onClick);
      host.addEventListener('change', onChangeEvent);
      host.addEventListener('keydown', onKeydown);
      bound = true;
      host.__mbfBound = true;
      host.__mbfDestroy = destroy;
    }

    function destroy() {
      if (!bound) { return; }
      host.removeEventListener('click', onClick);
      host.removeEventListener('change', onChangeEvent);
      host.removeEventListener('keydown', onKeydown);
      bound = false;
      delete host.__mbfBound;
      delete host.__mbfDestroy;
    }

    /* A previous instance on the same host (hot reload, theme editor section re-render) hands over cleanly instead of
       leaving two sets of listeners behind. */
    if (host.__mbfBound && typeof host.__mbfDestroy === 'function') { host.__mbfDestroy(); }

    return {
      render: render,
      /* Called with { param: { value: count } } just before render(), so the renderer can
         drop values the server says have no matches. */
      setCounts: function (map) { COUNTS = map || Object.create(null); },
      /* Call before render(). False on any source whose counts cannot be trusted - today that is
         /search, which Shopify caps at ~1000 on a catalogue of ~108k. The values still render, and
         still disable themselves when the server says zero; they just stop claiming a number. */
      setShowCounts: function (on) { SHOW_COUNTS = on !== false; },
      /* Exclusion is a RUNTIME fact, not a create-time one: a facet the page owns in one
         mode is an ordinary facet in another (materials, when the page shows every
         material). Call before render() - and before setAll(), which also honours it. */
      setExcluded: function (param, on) {
        if (!param) { return; }
        if (on) { EXCLUDE[param] = 1; } else { delete EXCLUDE[param]; }
      },
      all: allValues,
      setAll: setAll,
      get: get,
      count: count,
      chips: chips,
      clearAll: clearAll,
      clearGroup: clearGroup,
      priceParams: { min: PRICE_MIN, max: PRICE_MAX },
      destroy: destroy,
      /* debugging only */
      fills: function () { return FILLS; },
      groups: function () { return LAST; },
      openState: function () { return OPEN; }
    };
  }

  window.SWBFacetsRender = { create: create };
})(window, document);
